import { useState, useEffect, useCallback, useRef } from "react";
import { DealerLayout } from "@/components/shared/DealerLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { getMyDealerVisits, visitCheckIn, visitCheckOut, addVisitPhoto, uploadFile } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Calendar, Loader2, Clock, CheckCircle, XCircle, Play, Square,
  Camera, ClipboardList, MapPin, ChevronRight, Upload, Save, X,
} from "lucide-react";

const STATUS_CONFIG = {
  scheduled: { label: "Scheduled", color: "bg-blue-100 text-blue-700", icon: Calendar },
  "in-progress": { label: "In Progress", color: "bg-amber-100 text-amber-700", icon: Play },
  completed: { label: "Completed", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
  missed: { label: "Missed", color: "bg-red-100 text-red-700", icon: XCircle },
};

function VisitActionDialog({ visit, open, onClose, onUpdate }) {
  const [tasks, setTasks] = useState([]);
  const [notes, setNotes] = useState("");
  const [acting, setActing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (visit) {
      setTasks(visit.tasks?.map((t) => ({ ...t })) || []);
      setNotes(visit.notes || "");
    }
  }, [visit]);

  if (!visit) return null;

  const cfg = STATUS_CONFIG[visit.status] || STATUS_CONFIG.scheduled;
  const StatusIcon = cfg.icon;
  const completedTasks = tasks.filter((t) => t.completed).length;

  const handleCheckIn = async () => {
    setActing(true);
    try {
      await visitCheckIn(visit.id);
      toast.success("Checked in!");
      onUpdate();
    } catch (err) { toast.error("Failed to check in"); }
    finally { setActing(false); }
  };

  const handleCheckOut = async () => {
    setActing(true);
    try {
      await visitCheckOut(visit.id, { notes, tasks });
      toast.success("Checked out! Visit completed.");
      onUpdate();
      onClose();
    } catch (err) { toast.error("Failed to check out"); }
    finally { setActing(false); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const uploadResult = await uploadFile(file);
      await addVisitPhoto(visit.id, { url: uploadResult.url, caption: "" });
      toast.success("Photo uploaded!");
      onUpdate();
    } catch (err) { toast.error("Failed to upload photo"); }
    finally { setUploading(false); }
  };

  const toggleTask = (i) => {
    const next = [...tasks];
    next[i] = { ...next[i], completed: !next[i].completed };
    setTasks(next);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="visit-action-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-navy">Visit Details</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <span className={`px-2 py-0.5 text-xs rounded font-medium inline-flex items-center gap-1 ${cfg.color}`}>
                <StatusIcon className="w-3 h-3" /> {cfg.label}
              </span>
              <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded capitalize">{visit.visit_type}</span>
            </div>
            <p className="font-heading font-bold text-navy">{visit.location_name}</p>
            <p className="text-xs text-slate-500">{visit.location_address}</p>
            <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
              <Calendar className="w-3 h-3" /> {visit.scheduled_date}
            </p>
          </div>

          {/* Check-in/out times */}
          {(visit.check_in_time || visit.check_out_time) && (
            <div className="grid grid-cols-2 gap-3">
              <div className="p-2 bg-amber-50 rounded-lg text-center">
                <p className="text-xs text-slate-500">Check In</p>
                <p className="text-xs font-medium text-navy">{visit.check_in_time ? new Date(visit.check_in_time).toLocaleString() : "—"}</p>
              </div>
              <div className="p-2 bg-emerald-50 rounded-lg text-center">
                <p className="text-xs text-slate-500">Check Out</p>
                <p className="text-xs font-medium text-navy">{visit.check_out_time ? new Date(visit.check_out_time).toLocaleString() : "—"}</p>
              </div>
            </div>
          )}

          {/* Tasks */}
          {tasks.length > 0 && (
            <div>
              <p className="text-sm font-medium text-navy mb-2">Tasks ({completedTasks}/{tasks.length})</p>
              <div className="space-y-1.5">
                {tasks.map((t, i) => (
                  <button
                    key={i}
                    onClick={() => visit.status === "in-progress" ? toggleTask(i) : null}
                    className={`w-full flex items-center gap-2 p-2.5 rounded-lg text-sm text-left transition-colors ${t.completed ? "bg-emerald-50" : "bg-slate-50"} ${visit.status === "in-progress" ? "hover:bg-slate-100 cursor-pointer" : ""}`}
                    data-testid={`task-toggle-${i}`}
                  >
                    {t.completed ? <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" /> : <div className="w-4 h-4 rounded-full border-2 border-slate-300 flex-shrink-0" />}
                    <span className={t.completed ? "text-emerald-700 line-through" : "text-slate-700"}>{t.description}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {visit.status === "in-progress" && (
            <div>
              <p className="text-sm font-medium text-navy mb-1">Notes</p>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Add visit notes..." data-testid="visit-notes-input" />
            </div>
          )}

          {/* Photos */}
          {visit.photos?.length > 0 && (
            <div>
              <p className="text-sm font-medium text-navy mb-2">Photos ({visit.photos.length})</p>
              <div className="grid grid-cols-3 gap-2">
                {visit.photos.map((p) => (
                  <img key={p.id} src={p.url} alt="" className="w-full h-20 object-cover rounded-lg border border-slate-200" />
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="border-t pt-4 space-y-2">
            {visit.status === "scheduled" && (
              <Button className="w-full bg-amber-500 hover:bg-amber-600 text-white" onClick={handleCheckIn} disabled={acting} data-testid="check-in-btn">
                {acting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />} Check In
              </Button>
            )}
            {visit.status === "in-progress" && (
              <>
                <input type="file" ref={fileRef} accept="image/*" className="hidden" onChange={handlePhotoUpload} />
                <Button variant="outline" className="w-full" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-photo-btn">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Camera className="w-4 h-4 mr-2" />} Upload Photo
                </Button>
                <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white" onClick={handleCheckOut} disabled={acting} data-testid="check-out-btn">
                  {acting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Square className="w-4 h-4 mr-2" />} Check Out & Complete
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function DealerVisits() {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("upcoming");
  const [selectedVisit, setSelectedVisit] = useState(null);

  const fetchVisits = useCallback(async () => {
    try {
      const data = await getMyDealerVisits();
      setVisits(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchVisits(); }, [fetchVisits]);

  const upcoming = visits.filter((v) => v.status === "scheduled" || v.status === "in-progress");
  const completed = visits.filter((v) => v.status === "completed");
  const missed = visits.filter((v) => v.status === "missed");
  const current = tab === "upcoming" ? upcoming : tab === "completed" ? completed : missed;

  if (loading) {
    return (
      <DealerLayout title="My Visits">
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal" /></div>
      </DealerLayout>
    );
  }

  return (
    <DealerLayout title="My Visits">
      <div className="max-w-4xl mx-auto space-y-6" data-testid="dealer-visits-page">
        <div>
          <h1 className="font-heading font-bold text-2xl text-navy">My Visits</h1>
          <p className="text-sm text-slate-500 font-body mt-1">Your scheduled store visits and history</p>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{upcoming.length}</p>
              <p className="text-xs text-slate-500">Upcoming</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-emerald-600">{completed.length}</p>
              <p className="text-xs text-slate-500">Completed</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-red-500">{missed.length}</p>
              <p className="text-xs text-slate-500">Missed</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          {[
            { key: "upcoming", label: `Upcoming (${upcoming.length})`, icon: Calendar },
            { key: "completed", label: `Completed (${completed.length})`, icon: CheckCircle },
            { key: "missed", label: `Missed (${missed.length})`, icon: XCircle },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t.key ? "bg-teal text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}
              data-testid={`tab-${t.key}`}
            >
              <t.icon className="w-4 h-4 inline mr-1.5" />{t.label}
            </button>
          ))}
        </div>

        {/* Visit list */}
        <div className="space-y-3" data-testid="visits-list">
          {current.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="py-12 text-center">
                <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 font-body">No {tab} visits</p>
              </CardContent>
            </Card>
          ) : (
            current.map((visit, i) => {
              const cfg = STATUS_CONFIG[visit.status] || STATUS_CONFIG.scheduled;
              const StatusIcon = cfg.icon;
              const tasksDone = (visit.tasks || []).filter((t) => t.completed).length;
              const totalTasks = (visit.tasks || []).length;
              return (
                <motion.div key={visit.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                  <Card
                    className="border-slate-200 hover:border-teal/30 transition-colors cursor-pointer"
                    onClick={() => setSelectedVisit(visit)}
                    data-testid={`visit-card-${visit.id}`}
                  >
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${cfg.color}`}>
                        <StatusIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-heading font-semibold text-navy text-sm">{visit.location_name}</h3>
                          <span className={`px-2 py-0.5 text-xs rounded font-medium ${cfg.color}`}>{cfg.label}</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{visit.location_address}</p>
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400">
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {visit.scheduled_date}</span>
                          <span className="capitalize">{visit.visit_type}</span>
                          {totalTasks > 0 && <span>{tasksDone}/{totalTasks} tasks</span>}
                          {visit.photos?.length > 0 && <span>{visit.photos.length} photos</span>}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })
          )}
        </div>

        <VisitActionDialog
          visit={selectedVisit}
          open={!!selectedVisit}
          onClose={() => setSelectedVisit(null)}
          onUpdate={() => { fetchVisits(); setSelectedVisit(null); }}
        />
      </div>
    </DealerLayout>
  );
}
